import { useEffect, useRef, useState, useTransition } from 'react';
import {
  addDebtorNoteEntry,
  addMatterNoteEntry,
  deleteDebtorNoteEntry,
  deleteMatterNoteEntry,
  findDebtorProfileNotes,
  formatNoteWhen,
  listDebtorNoteEntries,
  listMatterNoteEntries,
} from '../lib/recordNotes.js';
import TrashIcon from './TrashIcon.jsx';
import { useSaveToast } from './SaveToast.jsx';
import { reportAppNotification } from '../lib/appNotifications.js';

function AddNoteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M12 5v14M5 12h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function loadEntries({ kind, recordId, debtor, email, user, initialEntries }) {
  if (Array.isArray(initialEntries)) return initialEntries;
  if (kind === 'debtor') {
    const fromProfile = findDebtorProfileNotes(email || user?.email, recordId || debtor?.id);
    if (fromProfile.length) return fromProfile;
    return listDebtorNoteEntries(debtor);
  }
  return listMatterNoteEntries(recordId);
}

/**
 * Dated note timeline with composer for demand or debtor records.
 */
export default function RecordNotesSection({
  kind = 'matter',
  recordId,
  debtor,
  user,
  email,
  disabled = false,
  initialEntries,
  onEntriesChange,
  onNoteAdded,
  onNoteDeleted,
  hideTitle = false,
  unframed = false,
  lead = 'Private comments for your team. Each note is saved with your name and a timestamp.',
  placeholder = 'Add a note…',
}) {
  const [draft, setDraft] = useState('');
  const [entries, setEntries] = useState(() =>
    loadEntries({ kind, recordId, debtor, email, user, initialEntries })
  );
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();
  const [deletingId, setDeletingId] = useState('');
  const textareaRef = useRef(null);
  const { flashSave } = useSaveToast();
  const canSubmit = Boolean(draft.trim()) && !pending && !disabled;

  useEffect(() => {
    setEntries(loadEntries({ kind, recordId, debtor, email, user, initialEntries }));
    setError('');
  }, [kind, recordId, debtor?.id, debtor?.notesEntries, email, user?.email, initialEntries]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  function refresh() {
    const next = loadEntries({ kind, recordId, debtor, email, user });
    setEntries(next);
    onEntriesChange?.(next);
  }

  function submitNote(e) {
    e?.preventDefault?.();
    if (disabled || pending) return;
    const body = draft.trim();
    if (!body) {
      setError('Enter a note before adding.');
      return;
    }
    setError('');
    startTransition(() => {
      const extras = {
        body,
        authorName: user?.displayName || user?.email || 'You',
        authorEmail: user?.email || email || '',
      };
      const added =
        kind === 'debtor'
          ? addDebtorNoteEntry(email || user?.email, debtor, extras)
          : addMatterNoteEntry(recordId, extras);
      if (!added) {
        setError('Could not save that note.');
        return;
      }
      setDraft('');
      onNoteAdded?.(added);
      flashSave('Note added.');
      refresh();
      void reportAppNotification({
        kind: 'newNote',
        matterId: kind === 'matter' ? recordId : '',
        debtorId: kind === 'debtor' ? recordId || debtor?.id || '' : '',
        debtorName: debtor?.company || debtor?.name || debtor?.companyName || '',
        noteId: added.id,
        snippet: added.body,
      });
    });
  }

  function onComposerKeyDown(e) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) return;
    e.preventDefault();
    if (disabled || pending || !draft.trim()) return;
    const form = e.currentTarget.form;
    if (typeof form?.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  function removeNote(note) {
    if (disabled || !note?.id || deletingId) return;
    const preview = String(note.body || '').trim();
    const snippet = preview.length > 80 ? `${preview.slice(0, 77)}…` : preview;
    const ok = window.confirm(
      snippet ? `Delete this note?\n\n“${snippet}”` : 'Delete this note?'
    );
    if (!ok) return;

    setError('');
    setDeletingId(note.id);
    startTransition(() => {
      const removed =
        kind === 'debtor'
          ? deleteDebtorNoteEntry(email || user?.email, debtor, note.id)
          : deleteMatterNoteEntry(recordId, note.id);
      setDeletingId('');
      if (!removed) {
        setError('Could not delete that note.');
        return;
      }
      onNoteDeleted?.(note);
      refresh();
    });
  }

  return (
    <section
      className={
        unframed
          ? 'record-notes-section is-unframed'
          : 'detail-section record-notes-section'
      }
    >
      {!hideTitle ? <h3>Notes</h3> : null}
      {lead ? <p className="settings-sublead">{lead}</p> : null}

      {!disabled ? (
        <form className="record-notes-composer" onSubmit={submitNote}>
          <div className="field field-span-2">
            <span className="sr-only">New note</span>
            <div className="record-notes-input-shell">
              <textarea
                ref={textareaRef}
                className="record-notes-input"
                rows={3}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (error) setError('');
                }}
                onKeyDown={onComposerKeyDown}
                placeholder={placeholder}
                aria-label="New note"
                aria-keyshortcuts="Enter"
                title="Press Enter to add · Shift+Enter for a new line"
              />
              <button
                type="submit"
                className="record-notes-add-icon"
                disabled={!canSubmit}
                aria-label={pending ? 'Adding note' : 'Add note'}
                title={pending ? 'Adding…' : 'Add note'}
              >
                {pending ? (
                  <span className="record-notes-add-pending" aria-hidden="true">
                    …
                  </span>
                ) : (
                  <AddNoteIcon />
                )}
              </button>
            </div>
          </div>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="drawer-actions record-notes-add-footer">
            <button type="submit" className="btn-primary" disabled={!canSubmit}>
              {pending ? 'Adding…' : 'Add Note'}
            </button>
          </div>
        </form>
      ) : error ? (
        <p className="form-error">{error}</p>
      ) : null}

      {entries.length ? (
        <ol className="record-notes-list">
          {entries.map((note) => (
            <li key={note.id} className="record-notes-item">
              <div className="record-notes-meta">
                <strong>{note.authorName}</strong>
                <span className="record-notes-meta-sep" aria-hidden="true">
                  •
                </span>
                <time dateTime={note.createdAt}>{formatNoteWhen(note.createdAt)}</time>
              </div>
              <div className="record-notes-content">
                <p className="record-notes-body">{note.body}</p>
                {!disabled ? (
                  <button
                    type="button"
                    className="record-notes-delete"
                    aria-label={deletingId === note.id ? 'Deleting note' : 'Delete note'}
                    title="Delete note"
                    disabled={Boolean(deletingId) || pending}
                    onClick={() => removeNote(note)}
                  >
                    {deletingId === note.id ? (
                      <span className="record-notes-delete-busy" aria-hidden="true">
                        …
                      </span>
                    ) : (
                      <TrashIcon size={16} />
                    )}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="empty-inline">No notes yet.</p>
      )}
    </section>
  );
}
